/**
 * Thread state management utilities
 */

/**
 * Optimized message sorting without global caching (thread-safe)
 * @param {Array} messages - Messages to sort
 * @returns {Array} - Sorted messages
 */
function safeSortMessages(messages) {
  if (!Array.isArray(messages) || messages.length <= 1) {
    return messages;
  }

  // Always create a new sorted array to avoid mutations
  return [...messages].sort((a, b) => {
    const dateA = new Date(
      a.created_at || a.timestamp || "1970-01-01T00:00:00.000Z"
    );
    const dateB = new Date(
      b.created_at || b.timestamp || "1970-01-01T00:00:00.000Z"
    );

    const timeDiff = dateA - dateB;

    // If timestamps are very close (within 1 second), prioritize by role
    if (Math.abs(timeDiff) < 1000) {
      // User messages should always come before assistant messages when timestamps are close
      if (a.role === "user" && b.role === "assistant") {
        return -1; // User message first
      }
      if (a.role === "assistant" && b.role === "user") {
        return 1; // Assistant message second
      }

      // If same role or neither is user/assistant, use message ID for stable sorting
      return a.id.localeCompare(b.id);
    }

    return timeDiff;
  });
}

/**
 * Helper function to safely update thread state without replacing messages
 * @param {Function|Object} threadStore - The thread store
 * @param {Object} updates - The state updates to apply
 */
export function safelyUpdateThreadState(threadStore, updates) {
  if (!threadStore) return;

  const setStoreState =
    typeof threadStore === "function" ? threadStore : threadStore.setState;

  // Get current state
  const currentState =
    typeof threadStore.getState === "function" ? threadStore.getState() : {};

  // Preserve messages from current state
  const currentMessages = currentState.messages || [];

  // Find all messages with metadata or tool_calls to preserve
  // CRITICAL: Include streaming messages to preserve reasoning metadata
  const messagesWithMetadata = currentMessages.filter(
    (msg) =>
      (msg.metadata && Object.keys(msg.metadata).length > 0) ||
      (msg.tool_calls && msg.tool_calls.length > 0) ||
      msg.isStreaming === true || // Always preserve metadata for streaming messages
      msg.role === "assistant" // Always preserve metadata for assistant messages
  );

  // CRITICAL FIX: If preventNewMessageClear is set in current state, don't allow message replacement
  const shouldPreventMessageReplacement =
    currentState.preventNewMessageClear === true ||
    updates.preventNewMessageClear === true;

  // Apply updates using a functional update to ensure we don't replace the messages array
  setStoreState((state) => {
    // Check if we need to preserve metadata on existing messages
    let updatedMessages = updates.messages || state.messages || currentMessages;

    // CRITICAL FIX: If we're in a protected state, prioritize current messages over updates
    if (
      shouldPreventMessageReplacement &&
      state.messages &&
      state.messages.length > 0
    ) {
      // If updates.messages is provided but we're in protected mode, merge intelligently
      if (updates.messages && Array.isArray(updates.messages)) {
        const stateMessages = state.messages;
        const updateMessages = updates.messages;

        // Check if update messages are actually newer/different
        const hasNewerContent = updateMessages.some((updateMsg) => {
          const stateMsg = stateMessages.find((sm) => sm.id === updateMsg.id);
          if (!stateMsg) return true; // New message

          // Check if the update message has newer content or completion state
          const isNewer =
            (updateMsg.isStreamComplete && !stateMsg.isStreamComplete) ||
            (updateMsg.content &&
              updateMsg.content.length > (stateMsg.content || "").length) ||
            (updateMsg.tool_calls && !stateMsg.tool_calls);

          return isNewer;
        });

        // Only use update messages if they contain genuinely newer content
        if (hasNewerContent) {
          updatedMessages = updateMessages;
        } else {
          updatedMessages = stateMessages;
        }
      } else {
        // No update messages provided, keep current state
        updatedMessages = state.messages;
      }
    }

    // CRITICAL: Never modify created_at timestamps of existing messages
    // The safeSortMessages function already handles missing timestamps by falling back to timestamp or default
    // This preserves the immutability of created_at fields

    // CRITICAL: Always preserve metadata for streaming messages and assistant messages
    // This ensures reasoning metadata isn't lost during streaming updates
    if (messagesWithMetadata.length > 0) {
      updatedMessages = updatedMessages.map((msg) => {
        // Find matching message with metadata in our tracked collection
        const msgWithMetadata = messagesWithMetadata.find(
          (m) => m.id === msg.id
        );
        if (msgWithMetadata) {
          // Check if this is a streaming update that should preserve reasoning metadata
          const isStreamingUpdate =
            msg.isStreaming === true || msgWithMetadata.isStreaming === true;
          const isAssistantMessage =
            msg.role === "assistant" || msgWithMetadata.role === "assistant";

          // Always preserve metadata for streaming messages and assistant messages
          if (
            isStreamingUpdate ||
            isAssistantMessage ||
            updates.preserveMetadata
          ) {
            // Preserve the metadata and tool_calls fields from the original message
            // BUT prioritize streaming state from the updated message
            const preservedMessage = {
              ...msgWithMetadata, // Start with old message (preserves original created_at)
              ...msg, // Override with new message properties (including isStreaming, isStreamComplete, etc.)
              metadata: {
                ...(msgWithMetadata.metadata || {}),
                ...(msg.metadata || {}), // New metadata takes precedence
              },
              tool_calls: msg.tool_calls || msgWithMetadata.tool_calls, // New tool_calls take precedence
              // CRITICAL: Always preserve the original created_at timestamp - NEVER modify it
              created_at: msgWithMetadata.created_at || msg.created_at,
              // Only update updated_at if it's actually being updated
              updated_at: msg.updated_at || msgWithMetadata.updated_at,
            };

            return preservedMessage;
          }
        }
        return msg;
      });
    }

    // CRITICAL: Use thread-safe sorting without global caching
    if (Array.isArray(updatedMessages) && updatedMessages.length > 0) {
      updatedMessages = safeSortMessages(updatedMessages);
    }

    return {
      ...state,
      ...updates,
      // CRITICAL: Always preserve messages from current state
      messages: updatedMessages,
      // Ensure preventNewMessageClear is set to true when requested
      preventNewMessageClear:
        updates.preventNewMessageClear !== false
          ? true
          : updates.preventNewMessageClear,
    };
  });
}
